import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { api, setCachedAccessToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { EnvelopeSimple, ArrowRight, ShieldCheck, Motorcycle } from "@phosphor-icons/react";
import { AuthShell } from "./ForgotPassword";
import { PasswordInput } from "../components/PasswordInput";

// Air12 internal login. ONE route, TWO modes:
//   Staff  (SUPER_ADMIN/CTRL/CTOP/CAP) → email + password + Staff email-OTP MFA (unchanged).
//   Rider  (RID)                       → passwordless email OTP (server-gated, session-bound).
// No separate /rider/login. Rider mode never shows password/forgot/reset/MFA/Google/Sender copy.
export default function StaffLogin() {
  const { session, profile, loading, refreshProfile } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("staff"); // "staff" | "rider"

  // Staff-mode state
  const [stage, setStage] = useState("password"); // "password" | "otp"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Rider-mode state
  const [rStage, setRStage] = useState("email"); // "email" | "otp"
  const [rEmail, setREmail] = useState("");
  const [rOtp, setROtp] = useState("");
  const [rMasked, setRMasked] = useState("");

  const rawRedirect = new URLSearchParams(window.location.search).get("redirect");
  const safeRedirect = rawRedirect && rawRedirect.startsWith("/") && !rawRedirect.startsWith("//") ? rawRedirect : null;
  const staffRedirect = safeRedirect || "/app";
  const riderRedirect = safeRedirect || "/rider";

  useEffect(() => {
    if (loading || !session || !profile) return;
    if (profile.role === "SENDER") { nav("/app", { replace: true }); return; }
    if (profile.mfa_verified) { nav(profile.role === "RID" ? riderRedirect : staffRedirect, { replace: true }); }
  }, [loading, session, profile, nav, staffRedirect, riderRedirect]);

  useEffect(() => {
    if (!cooldown) return;
    const id = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  if (loading) return <div className="p-10 mono text-sm">Loading…</div>;

  const resetToPassword = async () => {
    try { await supabase.auth.signOut(); } catch (_) { /* ignore */ }
    setCachedAccessToken(null);
    setStage("password"); setPassword(""); setOtp(""); setCooldown(0);
  };

  // ---------- STAFF MODE ----------
  const onStaffSignIn = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      setCachedAccessToken(data.session?.access_token || null);
      const ch = await api.post("/auth/mfa/challenge");
      setMaskedEmail(ch?.data?.masked_email || "");
      setStage("otp"); setOtp(""); setCooldown(60);
      toast.success("We sent a 6-digit code to your staff email.");
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "OTP_DELIVERY_FAILED") { toast.error("We couldn't send your verification code. Please try again shortly."); await resetToPassword(); }
      else if (code === "PASSWORD_REAUTH_REQUIRED") { toast.error("Please sign in again to continue."); await resetToPassword(); }
      else if (err?.response) { toast.error(err.response.data?.detail?.message || "We couldn't start verification. Please try again."); await resetToPassword(); }
      else { toast.error("Invalid email or password. If your account is inactive, contact an administrator."); }
    } finally { setBusy(false); }
  };

  const onStaffVerify = async (e) => {
    e.preventDefault();
    const code = otp.replace(/\D/g, "");
    if (!/^\d{6}$/.test(code)) { toast.error("Enter the 6-digit code."); return; }
    setBusy(true);
    try {
      await api.post("/auth/mfa/verify", { code });
      toast.success("Verified");
      await refreshProfile();
      nav(staffRedirect, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Invalid or expired code. Request a new code and try again.");
      setOtp("");
    } finally { setBusy(false); }
  };

  const onStaffResend = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    try { await api.post("/auth/mfa/resend"); setOtp(""); setCooldown(60); toast.success("A new code was sent. The previous code no longer works."); }
    catch (err) {
      if (err?.response?.data?.detail?.code === "PASSWORD_REAUTH_REQUIRED") { toast.error("Please sign in again to continue."); await resetToPassword(); }
      else { toast.error(err?.response?.data?.detail?.message || "We couldn't resend the code."); }
    } finally { setBusy(false); }
  };

  // ---------- RIDER MODE ----------
  const onRiderSend = async (e) => {
    e.preventDefault();
    const em = rEmail.trim();
    if (!em) { toast.error("Enter your registered Rider email."); return; }
    setBusy(true);
    try {
      // Pre-auth; non-enumerating: always advances to the code step on success.
      const res = await api.post("/auth/rider/otp/request", { email: em });
      setRMasked(res?.data?.masked_email || "");
      setRStage("otp"); setROtp(""); setCooldown(60);
      toast.success("If this is a registered Rider email, a 6-digit code has been sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "We couldn't send a code. Please try again shortly.");
    } finally { setBusy(false); }
  };

  const onRiderVerify = async (e) => {
    e.preventDefault();
    const code = rOtp.replace(/\D/g, "");
    if (!/^\d{6}$/.test(code)) { toast.error("Enter the 6-digit code."); return; }
    setBusy(true);
    try {
      const res = await api.post("/auth/rider/otp/verify", { email: rEmail.trim(), code });
      const { access_token, refresh_token } = res.data || {};
      if (!access_token || !refresh_token) throw new Error("mint_failed");
      const { error } = await supabase.auth.setSession({ access_token, refresh_token });
      if (error) throw error;
      setCachedAccessToken(access_token);
      toast.success("Signed in");
      await refreshProfile();
      nav(riderRedirect, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Invalid or expired code. Request a new code and try again.");
      setROtp("");
    } finally { setBusy(false); }
  };

  const onRiderResend = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    try { await api.post("/auth/rider/otp/resend", { email: rEmail.trim() }); setROtp(""); setCooldown(60); toast.success("A new code was sent. The previous code no longer works."); }
    catch (err) { toast.error(err?.response?.data?.detail?.message || "We couldn't resend the code."); }
    finally { setBusy(false); }
  };

  const switchMode = (m) => {
    if (m === mode) return;
    setMode(m); setBusy(false); setCooldown(0);
    setStage("password"); setOtp("");
    setRStage("email"); setROtp("");
  };

  const tabBase = "flex-1 text-sm font-semibold py-2 rounded-md transition-colors";
  const tabOn = "bg-[hsl(var(--foreground))] text-[hsl(var(--background))]";
  const tabOff = "text-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]";

  return (
    <AuthShell title="Air12 Login">
      <div className="flex gap-1 p-1 mb-5 rounded-lg bg-[hsl(var(--muted))]/10 border border-[hsl(var(--border))]" data-testid="login-mode-selector">
        <button type="button" className={`${tabBase} ${mode === "staff" ? tabOn : tabOff}`} onClick={() => switchMode("staff")} data-testid="staff-mode-tab">Staff</button>
        <button type="button" className={`${tabBase} ${mode === "rider" ? tabOn : tabOff}`} onClick={() => switchMode("rider")} data-testid="rider-mode-tab">Rider</button>
      </div>

      {mode === "staff" ? (
        <>
          <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted))] mb-4" data-testid="staff-login-note">
            <ShieldCheck size={16} weight="bold" />
            <span>Staff sign-in (email, password and a one-time code).</span>
          </div>
          {stage === "password" ? (
            <form onSubmit={onStaffSignIn} className="space-y-3" data-testid="staff-login-form">
              <label className="block">
                <div className="overline mb-1.5">STAFF EMAIL</div>
                <input type="email" className="input-field" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" data-testid="login-email-input" />
              </label>
              <label className="block">
                <div className="overline mb-1.5">PASSWORD</div>
                <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} autoComplete="current-password" data-testid="login-password-input" />
              </label>
              <button type="submit" className="btn-primary w-full justify-center mt-2" disabled={busy} data-testid="login-submit-button">
                <EnvelopeSimple size={14} weight="bold" /> {busy ? "Signing in…" : "Continue"} <ArrowRight size={14} weight="bold" />
              </button>
              <div className="text-right">
                <a href="/forgot-password" className="text-xs underline text-[hsl(var(--muted))]" data-testid="forgot-password-link">Forgot password?</a>
              </div>
            </form>
          ) : (
            <form onSubmit={onStaffVerify} className="space-y-3" data-testid="staff-mfa-form">
              <p className="text-sm text-[hsl(var(--muted))]" data-testid="mfa-sent-note">
                Enter the 6-digit code sent to <span className="font-semibold text-[hsl(var(--foreground))]">{maskedEmail || "your staff email"}</span>. It expires in 5 minutes.
              </p>
              <label className="block">
                <div className="overline mb-1.5">6-DIGIT CODE</div>
                <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} className="input-field tracking-[0.4em] text-center text-lg" value={otp}
                       onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} required data-testid="mfa-otp-input" />
              </label>
              <button type="submit" className="btn-primary w-full justify-center mt-2" disabled={busy} data-testid="mfa-verify-button">{busy ? "Verifying…" : "Verify & continue"} <ArrowRight size={14} weight="bold" /></button>
              <button type="button" className="btn-ghost w-full justify-center" disabled={busy || cooldown > 0} onClick={onStaffResend} data-testid="mfa-resend-button">{cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}</button>
              <button type="button" className="text-xs underline text-[hsl(var(--muted))]" onClick={resetToPassword} data-testid="mfa-use-another-account">Use a different account</button>
            </form>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted))] mb-4" data-testid="rider-login-note">
            <Motorcycle size={16} weight="bold" />
            <span>Rider sign-in for registered Air12 Riders. We'll email you a one-time code — no password needed.</span>
          </div>
          {rStage === "email" ? (
            <form onSubmit={onRiderSend} className="space-y-3" data-testid="rider-login-form">
              <label className="block">
                <div className="overline mb-1.5">RIDER EMAIL</div>
                <input type="email" className="input-field" value={rEmail} onChange={(e) => setREmail(e.target.value)} required autoComplete="email" data-testid="rider-email-input" />
              </label>
              <button type="submit" className="btn-primary w-full justify-center mt-2" disabled={busy} data-testid="rider-send-code-button">
                <EnvelopeSimple size={14} weight="bold" /> {busy ? "Sending…" : "Send code"} <ArrowRight size={14} weight="bold" />
              </button>
            </form>
          ) : (
            <form onSubmit={onRiderVerify} className="space-y-3" data-testid="rider-otp-form">
              <p className="text-sm text-[hsl(var(--muted))]" data-testid="rider-sent-note">
                Enter the 6-digit code sent to <span className="font-semibold text-[hsl(var(--foreground))]">{rMasked || "your Rider email"}</span>. It expires in 5 minutes.
              </p>
              <label className="block">
                <div className="overline mb-1.5">6-DIGIT CODE</div>
                <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} className="input-field tracking-[0.4em] text-center text-lg" value={rOtp}
                       onChange={(e) => setROtp(e.target.value.replace(/\D/g, "").slice(0, 6))} required data-testid="rider-otp-input" />
              </label>
              <button type="submit" className="btn-primary w-full justify-center mt-2" disabled={busy} data-testid="rider-verify-button">{busy ? "Verifying…" : "Verify & continue"} <ArrowRight size={14} weight="bold" /></button>
              <button type="button" className="btn-ghost w-full justify-center" disabled={busy || cooldown > 0} onClick={onRiderResend} data-testid="rider-resend-button">{cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}</button>
              <button type="button" className="text-xs underline text-[hsl(var(--muted))]" onClick={() => { setRStage("email"); setROtp(""); setCooldown(0); }} data-testid="rider-use-different-email">Use a different email</button>
            </form>
          )}
        </>
      )}
    </AuthShell>
  );
}
